import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useId } from 'react';
import { IconCalendar, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Popover } from './Popover.js';
import { cx } from '../utils/cx.js';
import {
  formatDateFr,
  maskFrDateInput,
  parseFlexibleDate,
  parseIsoDate,
  toIsoDate,
} from './datePickerParse.js';
import styles from './DatePicker.module.css';

export type DatePickerProps = {
  value?: string;
  onChange: (nextIsoDate: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  className?: string;
  placeholder?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

type DayCell = {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
};

const WEEK_DAYS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'] as const;
const MONTH_LABEL = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const MONTH_SHORT_FR = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(new Date(2000, i, 1)),
);

type PanelMode = 'days' | 'months' | 'years';

function monthFullyOutsideRange(year: number, monthIndex: number, min: Date | null, max: Date | null): boolean {
  const lastDay = new Date(year, monthIndex + 1, 0);
  const firstDay = new Date(year, monthIndex, 1);
  if (min && lastDay < min) return true;
  if (max && firstDay > max) return true;
  return false;
}

function yearFullyOutsideRange(year: number, min: Date | null, max: Date | null): boolean {
  const lastDay = new Date(year, 11, 31);
  const firstDay = new Date(year, 0, 1);
  if (min && lastDay < min) return true;
  if (max && firstDay > max) return true;
  return false;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function monthStart(dt: Date): Date {
  return new Date(dt.getFullYear(), dt.getMonth(), 1);
}

function addMonths(dt: Date, count: number): Date {
  return new Date(dt.getFullYear(), dt.getMonth() + count, 1);
}

function startGrid(dt: Date): Date {
  const first = monthStart(dt);
  const jsDay = first.getDay(); // 0 Sunday
  const mondayIndex = (jsDay + 6) % 7; // Monday=0
  return new Date(first.getFullYear(), first.getMonth(), first.getDate() - mondayIndex);
}

function buildCells(viewMonth: Date, min?: Date | null, max?: Date | null): DayCell[] {
  const out: DayCell[] = [];
  const start = startGrid(viewMonth);
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const disabled = (min && d < min) || (max && d > max) || false;
    out.push({
      iso: toIsoDate(d),
      day: d.getDate(),
      inMonth: d.getMonth() === viewMonth.getMonth(),
      disabled,
    });
  }
  return out;
}

export function DatePicker({
  value,
  onChange,
  label,
  hint,
  error,
  id,
  name,
  disabled,
  required,
  min,
  max,
  className,
  placeholder = 'JJ/MM/AAAA',
  style,
  ariaLabel,
}: DatePickerProps) {
  const autoId = useId();
  const triggerId = id ?? name ?? autoId;
  const hiddenId = `${triggerId}-value`;
  const errorId = `${triggerId}-error`;
  const parsedValue = useMemo(() => parseIsoDate(value), [value]);
  const minDate = useMemo(() => parseIsoDate(min), [min]);
  const maxDate = useMemo(() => parseIsoDate(max), [max]);
  const today = useMemo(() => new Date(), []);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelMode>('days');
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    monthStart(parsedValue ?? minDate ?? today),
  );
  /** First year shown in the year grid (12-year block). */
  const [yearBlockStart, setYearBlockStart] = useState(() => {
    const y = (parsedValue ?? minDate ?? today).getFullYear();
    return Math.floor(y / 12) * 12;
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  const [draft, setDraft] = useState(() => (parsedValue ? formatDateFr(parsedValue) : ''));

  useEffect(() => {
    if (focusedRef.current) return;
    setDraft(parsedValue ? formatDateFr(parsedValue) : '');
  }, [parsedValue]);

  const commitIso = (iso: string) => {
    if (iso !== (value ?? '')) onChange(iso);
  };

  const commitDraft = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setDraft('');
      commitIso('');
      return;
    }
    const dt = parseFlexibleDate(trimmed);
    if (!dt) {
      setDraft(parsedValue ? formatDateFr(parsedValue) : '');
      return;
    }
    setDraft(formatDateFr(dt));
    commitIso(toIsoDate(dt));
  };

  const onDraftChange = (raw: string) => {
    const isoPaste = parseIsoDate(raw.trim());
    if (isoPaste) {
      setDraft(formatDateFr(isoPaste));
      commitIso(toIsoDate(isoPaste));
      return;
    }
    const masked = maskFrDateInput(raw);
    setDraft(masked);
    if (!masked) {
      commitIso('');
      return;
    }
    const dt = parseFlexibleDate(masked);
    if (dt && masked.replace(/\D/g, '').length === 8) {
      commitIso(toIsoDate(dt));
    }
  };

  /** Stable refs — bare `parseIsoDate` returns new Date() each render and would retrigger open-sync effect → month nav snaps back. */
  useEffect(() => {
    if (!open) return;
    setViewMonth(monthStart(parsedValue ?? minDate ?? today));
    setPanel('days');
    const y = (parsedValue ?? minDate ?? today).getFullYear();
    setYearBlockStart(Math.floor(y / 12) * 12);
    const t = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLButtonElement>('button[class*="day"]:not(:disabled)');
      first?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, parsedValue, minDate, today]);

  const cells = useMemo(() => buildCells(viewMonth, minDate, maxDate), [viewMonth, minDate, maxDate]);

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={triggerId}>
          {label}
        </label>
      ) : null}

      <input type="hidden" id={hiddenId} name={name} value={value ?? ''} required={required} />

      <div className={styles.field}>
        <Popover
          opened={open}
          onChange={setOpen}
          portal
          modal
          align="start"
          portalMinWidth={280}
          portalZIndex="var(--wp-z-popover-over-modal)"
          toggleOnTargetClick={false}
          target={
          <div className={cx(styles.trigger, error && styles.error, disabled && styles.disabled)}>
            <input
              id={triggerId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              className={styles.triggerInput}
              placeholder={placeholder}
              value={draft}
              disabled={disabled}
              aria-label={ariaLabel ?? label}
              aria-invalid={!!error}
              aria-required={required || undefined}
              aria-describedby={error ? errorId : undefined}
              style={style}
              onChange={(e) => onDraftChange(e.currentTarget.value)}
              onFocus={() => {
                focusedRef.current = true;
                setOpen(false);
              }}
              onBlur={() => {
                focusedRef.current = false;
                commitDraft(draft);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                commitDraft(draft);
              }}
            />
            <button
              type="button"
              className={styles.calendarBtn}
              aria-label="Ouvrir le calendrier"
              aria-haspopup="dialog"
              aria-expanded={open}
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((v) => !v);
              }}
            >
              <IconCalendar size={16} stroke={1.8} className={styles.calendarIcon} aria-hidden />
            </button>
          </div>
        }
      >
        <div ref={panelRef} className={styles.panel} role="dialog" aria-label="Calendrier">
          {panel === 'days' ? (
            <>
              <div className={styles.header}>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label="Mois précédent"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => addMonths(m, -1));
                  }}
                >
                  <IconChevronLeft size={16} stroke={1.8} />
                </button>
                <button
                  type="button"
                  className={styles.monthLabelBtn}
                  aria-label="Choisir mois et année"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart(Math.floor(viewMonth.getFullYear() / 12) * 12);
                    setPanel('months');
                  }}
                >
                  {MONTH_LABEL.format(viewMonth)}
                </button>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label="Mois suivant"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => addMonths(m, 1));
                  }}
                >
                  <IconChevronRight size={16} stroke={1.8} />
                </button>
              </div>

              <div className={styles.weekdays}>
                {WEEK_DAYS.map((d) => (
                  <span key={d} className={styles.weekday}>
                    {d}
                  </span>
                ))}
              </div>

              <div className={styles.grid}>
                {cells.map((c) => {
                  const dt = parseIsoDate(c.iso);
                  const isSelected = dt && parsedValue ? sameDay(dt, parsedValue) : false;
                  const isToday = dt ? sameDay(dt, today) : false;
                  return (
                    <button
                      key={c.iso}
                      type="button"
                      className={cx(
                        styles.day,
                        !c.inMonth && styles.outside,
                        isSelected && styles.selected,
                        isToday && !isSelected && styles.today,
                      )}
                      disabled={c.disabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange(c.iso);
                        setOpen(false);
                      }}
                    >
                      {c.day}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {panel === 'months' ? (
            <>
              <div className={styles.header}>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label="Année précédente"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => new Date(m.getFullYear() - 1, m.getMonth(), 1));
                  }}
                >
                  <IconChevronLeft size={16} stroke={1.8} />
                </button>
                <button
                  type="button"
                  className={styles.monthLabelBtn}
                  aria-label="Choisir une année"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart(Math.floor(viewMonth.getFullYear() / 12) * 12);
                    setPanel('years');
                  }}
                >
                  {viewMonth.getFullYear()}
                </button>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label="Année suivante"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMonth((m) => new Date(m.getFullYear() + 1, m.getMonth(), 1));
                  }}
                >
                  <IconChevronRight size={16} stroke={1.8} />
                </button>
              </div>
              <div className={styles.monthYearGrid}>
                {MONTH_SHORT_FR.map((label, monthIndex) => {
                  const y = viewMonth.getFullYear();
                  const blocked = monthFullyOutsideRange(y, monthIndex, minDate, maxDate);
                  return (
                    <button
                      key={monthIndex}
                      type="button"
                      className={cx(
                        styles.monthYearCell,
                        viewMonth.getMonth() === monthIndex && styles.monthYearCellActive,
                      )}
                      disabled={blocked}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMonth(new Date(y, monthIndex, 1));
                        setPanel('days');
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {panel === 'years' ? (
            <>
              <div className={styles.header}>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label="Bloc d’années précédent"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart((s) => s - 12);
                  }}
                >
                  <IconChevronLeft size={16} stroke={1.8} />
                </button>
                <button
                  type="button"
                  className={styles.monthLabelBtn}
                  aria-label="Retour au choix du mois"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPanel('months');
                  }}
                >
                  {yearBlockStart} — {yearBlockStart + 11}
                </button>
                <button
                  type="button"
                  className={styles.navBtn}
                  aria-label="Bloc d’années suivant"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setYearBlockStart((s) => s + 12);
                  }}
                >
                  <IconChevronRight size={16} stroke={1.8} />
                </button>
              </div>
              <div className={styles.monthYearGrid}>
                {Array.from({ length: 12 }, (_, i) => yearBlockStart + i).map((year) => {
                  const blocked = yearFullyOutsideRange(year, minDate, maxDate);
                  return (
                    <button
                      key={year}
                      type="button"
                      className={cx(
                        styles.monthYearCell,
                        viewMonth.getFullYear() === year && styles.monthYearCellActive,
                      )}
                      disabled={blocked}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMonth(new Date(year, viewMonth.getMonth(), 1));
                        setPanel('months');
                      }}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
        </Popover>
      </div>

      {error ? (
        <span id={errorId} className={styles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}

